import React from 'react';
import { getVehicles, getDrivers } from '@/services/samsara';
import { useCurrentUser } from '@/components/auth/AuthProvider';
import { useRole } from '@/hooks/useRole';
import type { SamsaraVehicle } from '@/types';

export function useAssignedVehicles() {
  const user = useCurrentUser();
  const { isAdmin, isSupervisor, loading: roleLoading } = useRole();
  const [vehicles, setVehicles] = React.useState<SamsaraVehicle[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [noVehicleReason, setNoVehicleReason] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    if (roleLoading) return;

    async function load() {
      try {
        setNoVehicleReason(null);
        const allVehicles = await getVehicles();
        console.log('[Vehicles] Total vehicles from Samsara:', allVehicles);
        // For inspectors, find their assigned vehicle via Samsara driver record
        const drivers = await getDrivers();
        console.log('[Vehicles] Total drivers from Samsara:', drivers);

        // Admins and supervisors see all vehicles
        if (isAdmin || isSupervisor) {
          setVehicles(allVehicles);
          return;
        }

        

        // Match driver by email first, then fall back to username prefix
        const matchedDriver = drivers.find(d =>
          d.email?.toLowerCase() === user?.email.toLowerCase() ||
          d.username?.toLowerCase() === user?.email.split('@')[0].toLowerCase()
        );

        console.log('[Vehicles] User email:', user?.email);
        console.log('[Vehicles] Matched driver:', matchedDriver
          ? `${matchedDriver.name} (vehicleId: ${matchedDriver.vehicleId}, vehicleName: ${matchedDriver.vehicleName})`
          : 'none'
        );

        if (!matchedDriver) {
          console.warn('[Vehicles] No Samsara driver record found for', user?.email);
          console.warn('[Vehicles] Available driver emails (first 5):', drivers.slice(0, 5).map(d => d.email));
          setNoVehicleReason(
            `No Samsara driver profile matches ${user?.email}. Ask an admin to check that this email is set on your Samsara driver account.`
          );
          setVehicles([]);
          return;
        }

        let assignedVehicle: SamsaraVehicle | undefined;

        // 1. Match by Samsara vehicle ID (most reliable)
        if (matchedDriver.vehicleId) {
          assignedVehicle = allVehicles.find(v => v.id === matchedDriver.vehicleId);
          console.log('[Vehicles] ID match result:', assignedVehicle?.name ?? 'not found');
        }

        // 2. Exact name match (unit number e.g. "110")
        // Exact match avoids "110" incorrectly matching "1100"
        if (!assignedVehicle && matchedDriver.vehicleName) {
          assignedVehicle = allVehicles.find(v =>
            v.name.toLowerCase() === matchedDriver.vehicleName!.toLowerCase()
          );
          console.log('[Vehicles] Exact name match result:', assignedVehicle?.name ?? 'not found');
        }

        // 3. Prefix match as last resort (only if nothing else worked)
        if (!assignedVehicle && matchedDriver.vehicleName) {
          assignedVehicle = allVehicles.find(v =>
            v.name.toLowerCase().startsWith(matchedDriver.vehicleName!.toLowerCase())
          );
          console.log('[Vehicles] Prefix name match result:', assignedVehicle?.name ?? 'not found');
        }

        console.log('[Vehicles] Final assigned vehicle:', assignedVehicle?.name ?? 'none');

        if (!assignedVehicle) {
          setNoVehicleReason(
            matchedDriver.vehicleId || matchedDriver.vehicleName
              ? `Samsara driver profile "${matchedDriver.name}" is assigned to vehicle "${matchedDriver.vehicleName ?? matchedDriver.vehicleId}", but no matching vehicle was found in the Samsara fleet list. Check that this vehicle exists and isn't archived/renamed.`
              : `Samsara driver profile "${matchedDriver.name}" has no vehicle assignment set. Ask an admin to set a static assigned vehicle in Samsara.`
          );
        }

        setVehicles(assignedVehicle ? [assignedVehicle] : []);
      } catch (err: any) {
        console.error('[Vehicles] Failed to load vehicles:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [user?.id, isAdmin, isSupervisor, roleLoading]);

  return { vehicles, loading, error, noVehicleReason };
}